import { ReactNode } from 'react';

import { Collapse, Column, Link, RouteProps, Tooltip } from 'components/Blocks';

import { ChevronUpIcon, ChevronDownIcon, CircleAlertFilledIcon } from 'components/Icons';

import { useAppWrapper } from '../WrapperContext';

import { useToggle } from 'hooks';

import { cn } from 'styles';

interface ActionGroupProps<Item> {
  items: Item[];
  render: (item: Item) => JSX.Element;
  itemKey: keyof Item;
  icon: ReactNode;
  name: string;
  addErrorIndication?: boolean;

  /**
   * If the group will act as a Link to
   * some destination when the sidebar is
   * closed on desktop
   */
  minimizedLink?: RouteProps;

  itemClassName?: string;
}

export function ItemGroup<Item>({
  items,
  render,
  itemKey,
  name,
  icon,
  addErrorIndication,
  itemClassName,
  minimizedLink,
}: ActionGroupProps<Item>): JSX.Element | null {
  const { showIconOnly, toggleSideBar, isSideBarOpen } = useAppWrapper();

  const { isOn, toggle, turnOn } = useToggle();

  const showLink = !!minimizedLink && showIconOnly;

  const ClickTarget = showLink ? Link : 'button';

  const toggleMenu = () => {
    if (isSideBarOpen) return toggle();

    toggleSideBar();
    turnOn();
  };

  const targetProps = showLink ? minimizedLink! : { onClick: toggleMenu };

  return (
    <Column gap="0">
      <Tooltip
        position="right"
        arrow
        disabled={!showIconOnly}
        className="text-muted-foreground"
        content={name}
      >
        <ClickTarget
          {...(targetProps as RouteProps)}
          className={cn(
            'bg-primary-foreground text-primary hover:bg-primary-light hover:text-primary-foreground dark:bg-accent dark:text-accent-foreground dark:hover:bg-hover',
            'relative z-[2] inline-flex items-center justify-between gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors',
            showIconOnly &&
              'mx-auto gap-0 rounded-lg bg-transparent p-1 text-primary-foreground hover:bg-primary-light',
          )}
        >
          {icon}

          {!showIconOnly && (
            <>
              <span className={'relative'}>
                {name}

                {addErrorIndication && (
                  <CircleAlertFilledIcon
                    size={14}
                    color={'destructive-dark'}
                    className="absolute -right-3 -top-0.5"
                  />
                )}
              </span>

              {isOn ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </>
          )}
        </ClickTarget>
      </Tooltip>

      {!showIconOnly && (
        <Collapse expanded={isOn}>
          <Column
            gap="0"
            className="-translate-y-4 overflow-hidden rounded-b-3xl rounded-t-lg border border-primary-foreground dark:border-border"
          >
            {items.map((item, index) => (
              <div
                className={cn(
                  'p-3 odd:bg-primary-light even:bg-[#0695fd] dark:odd:bg-accent/50 dark:even:bg-accent',
                  itemClassName,
                  !index && 'pt-7',
                )}
                key={item[itemKey] as string}
              >
                {render(item)}
              </div>
            ))}
          </Column>
        </Collapse>
      )}
    </Column>
  );
}
